import dialogPolyfill from 'dialog-polyfill';
import util from '../utils/util';

const LEGACY_CONTAINER_ID = 'dialog-container';
const DIALOG_ID = 'storefront-dialog';

/**
 * @typedef {Object} DialogSettings
 * @property {boolean} [isModal=true] - whether to show the dialog as modal
 * @property {boolean} [useLegacyContainer=false] - whether to use container from legacy dialog
 * @property {boolean} [useLegacyBackdrop=false] - whether to use backdrop from legacy dialog
 * @property {boolean} [hasTitleBar=false] - whether to use backdrop from legacy dialog
 * @property {Array} [backdropClasses=[]] - specify additional backdrop classes
 * @property {Array} [additionalClasses=[]] - specify additional dialog classes
 * @property {HTMLElement} [target] - optional dialog element override target
 * @property {string} [url] - open dialog with downloaded content from URL. Either `url` or `html` is necessary
 * @property {Object} [data] - request data object when loading content from remote URL via the `url` property
 * @property {string} [html] - open dialog with the provided HTML. Either `url` or `html` is necessary.
 * @property {boolean} [canClose=false] - allow closing of the dialog by firing the `cancel` event
 * @property {boolean} [canCloseOnBackdropClick=true] - allow closing the dialog when click on the backdrop
 * @property {Function} [callback] - callback function to execute when the dialog is opened
 * @property {Function} [closeCallback] - closeCallback function to execute when the dialog is closed
 * @property {boolean} [hasCloseText=true] - whether the  close mark has text
 */

/**
 *  Class wrapping the native `<dialog>` element.
 */
export default class Dialog {
    /** @type {DialogSettings} */
    #settings = {
        isModal: true,
        useLegacyContainer: false,
        useLegacyBackdrop: false,
        backdropClasses: [],
        additionalClasses: [],
        target: null,
        url: null,
        data: null,
        html: null,
        canClose: false,
        callback: null,
        closeCallback: null,
        canCloseOnBackdropClick: true,
        hasTitleBar: false,
        hasCloseText: true
    };

    /** @type {HTMLDialogElement } */
    #dialog = null;

    /** @param {DialogSettings} settings - dialog settings besides the default values */
    constructor(settings) {
        Object.assign(this.#settings, settings);
        let dialog = this.#dialog;
        if (this.#dialog === null) {
            if (this.#settings.target) {
                dialog = this.#settings.target;
            } else {
                dialog = document.getElementById(DIALOG_ID);
            }
        }

        if (dialog === null) {
            dialog = document.createElement('dialog');
            // TODO these are legacy classes to inherit CSS!
            const legacyClasses = ['ui-dialog', 'ui-widget', 'ui-widget-content', 'ui-corner-all'];
            dialog.classList.add(...legacyClasses);
            dialog.id = `${DIALOG_ID}-${new Date().getTime()}`;
            document.body.appendChild(dialog);
        }

        this.#dialog = dialog;
        dialogPolyfill.registerDialog(this.#dialog);

        if (this.#settings.additionalClasses.length) {
            this.#dialog.classList.add(...this.#settings.additionalClasses);
        }

        this.#dialog.addEventListener('cancel', e => {
            e.preventDefault();
            if (this.#settings.canClose) {
                this.close();
            }
        });
        this.#dialog.addEventListener('click', event => {
            if (this.#settings.canCloseOnBackdropClick && event.target === this.#dialog) {
                event.preventDefault();
                this.close();
            }
        });
    }

    /** @param {{html: string, callback?: Function}} settings - load content from `html` and fire the optional `callback` */
    #openWithContent(settings) {
        if (!this.#dialog || !settings.html) {
            return;
        }

        if (!this.#dialog.open) {
            if (this.#settings.useLegacyBackdrop) {
                const backdrop = document.createElement('div');
                // TODO these are legacy classes to inherit CSS!
                const legacyBackdropClasses = ['ui-widget-overlay', 'ui-front'];
                backdrop.classList.add(...legacyBackdropClasses);

                if (this.#settings.backdropClasses.length) {
                    backdrop.classList.add(...this.#settings.backdropClasses);
                }
                this.#dialog.insertAdjacentElement('afterend', backdrop);
            }

            if (this.#settings.hasTitleBar) {
                const titleBar = document.createElement('div');
                const titleBarClasses = ['ui-dialog-titlebar', 'ui-widget-header', 'ui-helper-clearfix'];
                titleBar.classList.add(...titleBarClasses);

                const closeButton = document.createElement('button');
                const closeButtonClasses = ['ui-dialog-titlebar-close'];
                closeButton.classList.add(...closeButtonClasses);

                const closeIcon = document.createElement('span');
                const closeIconClasses = ['ui-icon', 'ui-icon-closethick'];
                closeIcon.classList.add(...closeIconClasses);

                closeButton.appendChild(closeIcon);
                if (this.#settings.hasCloseText) {
                    closeButton.append(window.Resources.CLOSE);
                }
                titleBar.appendChild(closeButton);
                this.#dialog.appendChild(titleBar);

                this.#dialog.querySelector('.ui-dialog-titlebar-close').addEventListener('click', event => {
                    event.preventDefault();
                    this.close();
                });
            }

            if (this.#settings.useLegacyContainer) {
                const container = document.createElement('div');
                container.id = LEGACY_CONTAINER_ID;
                // TODO these are legacy classes to inherit CSS!
                const legacyContainerClasses = ['dialog-content', 'ui-dialog-content', 'ui-widget-content'];
                container.classList.add(...legacyContainerClasses);

                this.#dialog.appendChild(container);
            }
        }

        if (this.#settings.useLegacyContainer) {
            this.#dialog.querySelector(`#${LEGACY_CONTAINER_ID}`).innerHTML = settings.html;
        } else {
            this.#dialog.innerHTML = settings.html;
        }

        if (!this.#dialog.open) {
            if (this.#settings.isModal) {
                this.#dialog.showModal();
            } else {
                this.#dialog.show();
            }
        }

        if (typeof settings.callback === 'function') {
            settings.callback();
        }
    }

    close() {
        if (this.#dialog === null || !this.#dialog.open) {
            return;
        }

        if (typeof this.#settings.closeCallback === 'function') {
            this.#settings.closeCallback();
        }

        this.#dialog.close();
        this.#dialog.innerHTML = '';
        if (this.#settings.additionalClasses.length) {
            this.#dialog.classList.remove(...this.#settings.additionalClasses);
        }

        if (this.#settings.useLegacyBackdrop) {
            document.querySelector('.ui-widget-overlay')?.remove();
        }
    }

    /**
     * @param {{url?: string, data?: object, html?: string, callback?: Function}} settings - load content either from remote via the `url`
     *  property or by already provided HTML in via the `html` property
     */
    loadContent(settings) {
        if (this.#dialog === null) {
            return;
        }

        if (settings.url) {
            $.ajax({
                type: 'GET',
                url: util.appendParamToURL(settings.url, 'format', 'ajax'),
                data: settings.data,
                /**
                 * @param {string} response - HTML content
                 */
                success: response => {
                    settings.html = response;
                    this.#openWithContent(settings);
                }
            });
        } else if (settings.html) {
            this.#openWithContent(settings);
        }
    }

    open() {
        this.close();
        this.loadContent(this.#settings);
    }

    isActive() {
        return this.#dialog !== null && this.#dialog.open && this.#dialog.childElementCount > 0;
    }

    get dialog() {
        return this.#dialog;
    }
}
